import { Edit05, SearchLg } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import ButtonCmp from 'src/components/ButtonCmp';
import CommonModal from 'src/components/CommonModal';
import InputText from 'src/components/InputText/InputText';
import OrbAvatar from 'src/components/OrbAvatar';

export interface IConversationHistoryItem {
    id: number;
    customerId: string;
    displayId: string;
    displayLabel: string;
    lastActivityAt: string;
}

interface IConversationGroup {
    label: string;
    items: IConversationHistoryItem[];
}

interface IProps {
    handleClose: () => void;
    onSelectTask: (item: IConversationHistoryItem) => void;
    onNewConversation: () => void;
}

const MOCK_CONVERSATIONS: IConversationHistoryItem[] = [
    { id: 1, customerId: 'K-78532', displayId: '#SQ-TCBC-766', displayLabel: 'New Quote Request', lastActivityAt: moment().subtract(2, 'hours').toISOString() },
    { id: 2, customerId: 'K-78532', displayId: '#SQ-TCBC-766', displayLabel: 'New Quote Request', lastActivityAt: moment().subtract(2, 'days').toISOString() },
    { id: 3, customerId: 'K-69541', displayId: '#WAL-TMTC-451', displayLabel: 'Tracking Updates', lastActivityAt: moment().subtract(4, 'days').toISOString() },
    { id: 4, customerId: 'K-69541', displayId: '#WAL-TMTC-451', displayLabel: 'Order Confirmation', lastActivityAt: moment().subtract(2, 'weeks').toISOString() },
    { id: 5, customerId: 'K-69541', displayId: 'SQ-TC8C-455', displayLabel: 'New Load Posting', lastActivityAt: moment().subtract(3, 'weeks').toISOString() },
    { id: 6, customerId: 'K-78532', displayId: '#SQ-TCBC-766', displayLabel: 'Tracking Updates', lastActivityAt: moment().subtract(4, 'weeks').toISOString() },
    { id: 7, customerId: 'K-69541', displayId: '#WAL-TMTC-451', displayLabel: 'New Quote Request', lastActivityAt: moment().subtract(5, 'weeks').toISOString() },
];

const ConversationHistoryModal = ({ handleClose, onSelectTask, onNewConversation }: IProps) => {
    const [searchValue, setSearchValue] = useState('');

    const conversationGroups = useMemo<IConversationGroup[]>(() => {
        const search = searchValue.trim().toLowerCase();
        const allItems = MOCK_CONVERSATIONS.filter((item) => !search || `${item.customerId} ${item.displayId} ${item.displayLabel}`.toLowerCase().includes(search)).sort(
            (a, b) => moment(b.lastActivityAt).valueOf() - moment(a.lastActivityAt).valueOf()
        );

        const now = moment();
        const groups: IConversationGroup[] = [
            { label: 'Today', items: [] },
            { label: 'This Week', items: [] },
            { label: 'This Month', items: [] },
            { label: 'This Year', items: [] },
            { label: 'Older', items: [] },
        ];

        allItems.forEach((item) => {
            const date = moment(item.lastActivityAt);
            if (date.isSame(now, 'day')) groups[0].items.push(item);
            else if (date.isSame(now, 'week')) groups[1].items.push(item);
            else if (date.isSame(now, 'month')) groups[2].items.push(item);
            else if (date.isSame(now, 'year')) groups[3].items.push(item);
            else groups[4].items.push(item);
        });

        return groups.filter((g) => g.items.length > 0);
    }, [searchValue]);

    return (
        <CommonModal title="" handleClose={handleClose} size="max-w-[640px] h-[65vh]" modalClassName="space-y-4 [&>*]:px-2.5 flex flex-col">
            <div className="pt-3">
                <div className="flex items-center relative px-1.5">
                    <div className="flex-1">
                        <div className="relative flex-1">
                            <div className="relative flex items-center">
                                <InputText
                                    parentClassName="w-full"
                                    inputName="searchConversation"
                                    placeholder="Search"
                                    className="pl-8"
                                    inputWrapClass=""
                                    icon={<SearchLg className="absolute -translate-y-2/4 top-2/4 left-2.5 text-grayText h-[16px] w-[16px]" />}
                                    isClearable={true}
                                    searchIconClass="!right-[70px]"
                                    value={searchValue}
                                    inputType="text"
                                    onChangeFunc={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e?.target?.value ?? '')}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <ButtonCmp
                    className="btn_neutral w-full justify-start !px-1.5"
                    size="square-md"
                    onClick={() => {
                        onNewConversation();
                        handleClose();
                    }}
                    icon={<Edit05 className="w-4 h-4" />}
                >
                    New Conversation
                </ButtonCmp>
            </div>

            <div className="pb-2 flex flex-col flex-1 overflow-auto space-y-3 custom-scrollbar-v2">
                {conversationGroups.length === 0 ? (
                    <p className="text-gray500 text-xs leading-[1.5] font-normal text-center pt-4">No conversations found</p>
                ) : (
                    conversationGroups.map((group) => (
                        <div key={group.label} className="space-y-1">
                            <h6 className="text-gray500 text-xs leading-[1.5] font-normal px-1.5">{group.label}</h6>
                            <ul>
                                {group.items.map((item) => (
                                    <li key={item.id}>
                                        <ButtonCmp
                                            className="btn_neutral w-full justify-start flex items-center gap-x-1 !px-1.5 !py-[5px]"
                                            size="md"
                                            onClick={() => {
                                                onSelectTask(item);
                                                handleClose();
                                            }}
                                        >
                                            <figure className="shrink-0">
                                                <OrbAvatar size={20} className="border border-utilityBlack border-opacity-[0.08] rounded-full bg-white" />
                                            </figure>
                                            <h6 className="text-textSecondary text-xs leading-[1.5] font-medium truncate">{item.displayLabel}</h6>
                                            <div className="text-gray500 text-[10px] leading-[14px] font-normal shrink-0">
                                                {item.customerId} <span className="text-gray400">•</span> {item.displayId}
                                            </div>
                                        </ButtonCmp>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))
                )}
            </div>

            <div className="border-t border-utilityGray200 !px-4 py-3 flex items-center !mt-0">
                <div className="flex items-center gap-x-1.5 flex-1 justify-between w-full">
                    <div className="rounded-md border border-utilityGray200 px-2 py-1 text-gray500 text-xs font-medium cursor-pointer hover:border-utilityGray700" onClick={handleClose}>
                        Esc
                    </div>
                </div>
            </div>
        </CommonModal>
    );
};

export default ConversationHistoryModal;
